<template>
  <div class="related-articles-container">
    <div class="related-articles-title">
      <slot name="title"></slot>
    </div>
    <div class="articles">
      <div
        v-for="article in localRelatedArticles"
        :key="article.id"
        class="article"
      >
        <a :href="article.url" class="article-link">
          <div class="article-wrapper" style="height: 100%">
            <div v-if="article.image">
              <div class="article-img-wrapper">
                <img :src="article.image" :srcset="article.imageSrcset" />
              </div>
            </div>
            <div class="article-bottom">
              <div class="article-title">{{ article.title }}</div>
              <div class="article-description">{{ article.text }}</div>
              <div v-if="article.type === 'DOSSIER'" class="article-dossier">
                <button class="article-dossier-button">
                  {{ article.tag }}
                </button>
                <span
                  v-if="article.numberOfArticlesInDossier"
                  class="article-dossier-count"
                >
                  {{
                    $t('articleCount', {
                      count: article.numberOfArticlesInDossier,
                    })
                  }}
                </span>
              </div>
            </div>
          </div>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { provideI36n, useI36n } from '@jota-one/i36n/src/index.vue2'

export default {
  name: 'SanitasMagazineRelatedArticles',
  props: {
    relatedArticles: {
      type: String,
      default: '',
    },
    language: {
      type: String,
      default: 'de',
    },
  },
  setup(props) {
    const loadLabels = lang => import(`../i18n/${lang}.json`)
    provideI36n(props.language, { load: loadLabels })
    const { $label } = useI36n()

    return {
      $t: $label,
    }
  },
  data() {
    return {
      localRelatedArticles: [],
    }
  },
  mounted() {
    if (this.relatedArticles) {
      this.localRelatedArticles = JSON.parse(this.relatedArticles)
    }
  },
}
</script>

<style lang="postcss">
.related-articles {
  &-title {
    font-family: 'Faustina', serif;
    font-size: 20px;
    font-weight: bold;
    letter-spacing: 0.4px;
    color: var(--c-primary-neutral-1);
    padding: 40px 0 20px;
  }

  &-container {
    display: flex;
    flex-direction: column;
    text-align: left;
    margin-bottom: 20px;
    min-height: 470px;

    sup {
      top: -0.2em !important;
    }

    .articles {
      display: flex;
      flex-wrap: wrap;
      flex-direction: row;
      justify-content: space-between;

      .article {
        width: calc((100% - 36px) / 3);
        display: flex;
        flex-wrap: wrap;
        flex-direction: row;
        padding-bottom: 12px;

        &-link {
          display: block;
          width: 100%;

          &:hover {
            border-bottom: none;
          }
        }

        &-wrapper {
          transition: 0.3s;
          border: 1px solid var(--c-secondary-neutral-3);
          display: flex;
          flex-direction: column;
          flex: 1;

          &:hover {
            box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.1);

            .article-title {
              color: var(--c-primary-color-2);
            }

            .article-dossier-button {
              color: var(--c-primary-neutral-3);
              background-color: var(--c-primary-color-3);
              border-color: var(--c-primary-color-3);
            }

            .article-dossier-count {
              color: var(--c-primary-color-3);
            }
          }
        }

        &-img-wrapper {
          position: relative;
          overflow: hidden;
          padding-top: 61.5385%;

          img {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            border: 0;
            object-fit: cover;
          }
        }

        &-bottom {
          display: flex;
          flex-direction: column;
          flex: 1 1 auto;
          overflow-wrap: break-word;
          min-height: 220px;
        }

        &-dossier {
          padding: 0 20px 20px 20px;
        }

        &-dossier-button {
          transition: 0.3s;
          height: 20px;
          font-size: 10px;
          font-weight: bold;
          text-align: center;
          color: var(--c-primary-color-3);
          background-color: var(--c-primary-neutral-3);
          border: 1px solid var(--c-primary-color-3);
          margin-right: 10px;
        }

        &-dossier-count {
          font-size: 10px;
          display: inline-block;
          color: var(--c-primary-color-3);
        }

        &-title {
          font-size: 15px;
          font-weight: bold;
          text-align: left;
          color: var(--c-primary-neutral-1);
          margin: 20px 20px 0 20px;
          line-height: 20px;
        }

        &-description {
          font-size: 15px;
          color: var(--c-primary-neutral-1);
          padding: 0 20px 10px 20px;
          min-height: 60px;
          flex: 1 1 auto;
          font-weight: normal;
          line-height: 20px;
        }

        &-url {
          font-size: 15px;
          text-decoration: none;
          font-weight: bold;
          padding: 10px 20px 20px 20px;

          a {
            color: white;
            text-decoration: underline;
          }

          a:hover {
            color: var(--c-primary-color-3);
            border-bottom: none;
          }
        }
      }
    }
  }
}

@media (max-width: 720px) {
  .related-articles {
    &-container {
      .articles {
        .article {
          width: calc((100% - 15px) / 2);
          margin-bottom: 20px;
          padding: 0;
        }
      }
    }
  }
}

@media (max-width: 490px) {
  .related-articles {
    &-container {
      .articles {
        flex-direction: column;

        .article {
          width: 100%;

          &-bottom {
            position: static;
          }
        }
      }
    }
  }
}
</style>
